import { useEffect, useRef, useState } from 'react'

const SHORTCUTS: [string, string][] = [
  ['↑ ↓ ← →', 'Move the cell cursor'],
  ['Enter', 'Edit the focused cell'],
  ['⌘/Ctrl + Enter', 'Save the cell and leave edit mode'],
  ['Tab', 'Save and move to the next cell'],
  ['Esc', 'Cancel the edit (or close a dialog)'],
  ['/', 'Focus the search box'],
  ['x', 'Select / deselect the focused row'],
  ['e', 'Expand the focused row (plurals + context)'],
  ['h', 'Open history for the focused cell'],
  ['r', 'Re-translate the focused cell with AI'],
  ['?', 'Toggle this help'],
]

export function HelpPopover() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const typing =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable
      if (event.key === '?' && !typing) {
        event.preventDefault()
        setOpen((value) => !value)
      }
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (!open) return
    const onClick = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  return (
    <div className="relative" ref={ref}>
      <button
        className="btn"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden="true">⌘</span> Shortcuts
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Keyboard shortcuts"
          className="absolute right-0 top-9 z-50 w-80 rounded-lg border border-ink-200 bg-white p-3 shadow-2xl"
        >
          <h3 className="mb-2 text-xs font-semibold text-ink-900">Keyboard shortcuts</h3>
          <dl className="space-y-1">
            {SHORTCUTS.map(([keys, description]) => (
              <div key={keys} className="flex items-baseline justify-between gap-3 text-xs">
                <dt>
                  <kbd className="rounded border border-ink-300 bg-ink-50 px-1 font-mono text-2xs text-ink-700">
                    {keys}
                  </kbd>
                </dt>
                <dd className="flex-1 text-right text-ink-600">{description}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-2 border-t border-ink-100 pt-2 text-2xs text-ink-600">
            Cells are keyboard-first: nothing in the grid needs the mouse.
          </p>
        </div>
      )}
    </div>
  )
}
